import { useField, useFormState } from "react-final-form"
import { InputValidationErrorMessage, Alert } from "@sembark-travel/ui/base"
import { useFieldArray } from "react-final-form-arrays"
import { getFieldArrayErrorName } from "./Form"

export function ErrorMessage({ name }: { name: string }) {
	const {
		meta: { error, touched, submitError },
	} = useField(name, {
		subscription: { error: true, touched: true, submitError: true },
	})
	const errorArrayFieldName = getFieldArrayErrorName(name)
	const {
		meta: { error: arrayFieldError },
	} = useField(errorArrayFieldName, {
		subscription: { error: true },
	})
	const dotedFieldName = name.replace(/\[/g, ".").replace(/\]/g, "")
	return (
		<>
			{((error || arrayFieldError) && touched) || submitError ? (
				<InputValidationErrorMessage>
					{submitError ? <RenderError message={submitError} /> : null}
					{error ? (
						<RenderError message={error} />
					) : arrayFieldError ? (
						<RenderError message={arrayFieldError} />
					) : null}
				</InputValidationErrorMessage>
			) : null}
			{name !== dotedFieldName ? (
				<FieldSubmitErrorMessage name={dotedFieldName} />
			) : null}
		</>
	)
}

export function FieldArrayErrorMessage({ name }: { name: string }) {
	const errorName = getFieldArrayErrorName(name)
	const {
		// The array field level errors are stored in `errorName`, instead of `name` to store both array field level errors
		// Check the `validateFormValues` fn in `Form.tsx` for more details
		meta: { error },
	} = useFieldArray(errorName, {
		subscription: { error: true },
	})
	const {
		fields: { length },
		meta: { touched, submitError }, // submit errors are stored in same key as we can not determine if a field is an array
	} = useFieldArray(name, {
		subscription: {
			touched: true,
			submitError: true,
			length: true,
		},
	})
	const dotedFieldName = name.replace(/\[/g, ".").replace(/\]/g, "")
	return (
		<>
			{(error && touched) || (submitError && length) ? (
				<InputValidationErrorMessage>
					{submitError ? <RenderError message={submitError} /> : null}
					{error ? <RenderError message={error} /> : null}
				</InputValidationErrorMessage>
			) : null}
			{name !== dotedFieldName ? (
				<FieldSubmitErrorMessage name={dotedFieldName} />
			) : null}
		</>
	)
}

function FieldSubmitErrorMessage({ name }: { name: string }) {
	const { submitErrors } = useFormState({
		subscription: { submitErrors: true },
	})
	return (
		<>
			{submitErrors && submitErrors[name] ? (
				<InputValidationErrorMessage>
					<RenderError message={submitErrors[name]} />
				</InputValidationErrorMessage>
			) : null}
		</>
	)
}

function RenderError({ message }: { message?: unknown }) {
	if (!message) return null
	if (typeof message === "string" || typeof message === "number") {
		return <>{message}</>
	}
	if (Array.isArray(message)) {
		return (
			<>
				{message.map((m, i) => (
					<RenderError key={i} message={m} />
				))}
			</>
		)
	}
	return null
}

export function SubmissionError() {
	const { submitError } = useFormState({ subscription: { submitError: true } })
	return submitError ? (
		<Alert status="error" hideIcon>
			<RenderError message={submitError} />
		</Alert>
	) : null
}
